import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { StickyNote, Pin, PinOff, Plus, Trash2, Loader2, ImagePlus, X, FileText } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { getToken as getAuthToken } from '../../lib/authStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/notes`;
const MAX_IMG_BYTES = 5 * 1024 * 1024; // 5MB per pasted image
const MAX_IMAGES = 6;

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result || ''));
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(file);
  });
}

// Kinfolk operates out of Arizona — every note timestamp displays in
// America/Phoenix (MST, no DST) regardless of the viewer's browser
// timezone. Prevents mismatches when an LO's laptop clock/tz is off.
const TZ = 'America/Phoenix';

/**
 * NoteBody — renders the note text as-is EXCEPT when it contains a
 * mortgage-statement URL on its own line. In that case we rewrite the
 * body to hide the raw link and replace it with an inline "Open
 * mortgage statement" button that opens the authenticated endpoint.
 *
 * This is how a Wingman-authored mortgage statement reference becomes
 * clickable from the Lead Profile notes screen without exposing API
 * paths to the LO.
 */
function NoteBody({ body }) {
  const text = body || '';
  const match = text.match(/\/api\/grok\/leads\/([^\s]+)\/mortgage-statement/);
  if (!match) return text;

  // Strip the "View: /api/grok/..." line from the visible body.
  const cleaned = text
    .replace(/\n*View:\s*\/api\/grok\/leads\/[^\s]+\/mortgage-statement\s*/g, '')
    .trim();

  const leadId = match[1];
  const openStatement = () => {
    const tok = getAuthToken();
    if (!tok) { toast.error('Session expired — please log back in.'); return; }
    const url = `${BACKEND_URL}/api/grok/leads/${leadId}/mortgage-statement?auth=${encodeURIComponent(tok)}`;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <>
      {cleaned}
      <button
        type="button"
        onClick={openStatement}
        className="mt-2 inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-neutral-900 bg-white border hairline hover:bg-neutral-100 px-2.5 py-1 rounded-md"
        data-testid="note-open-mortgage-statement"
      >
        <FileText className="h-3 w-3" />
        Open mortgage statement
      </button>
    </>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { timeZone: TZ, month: 'short', day: 'numeric' });
}

// Absolute date+time stamp shown alongside the relative timeAgo, e.g. "May 8, 4:23 PM"
function formatStamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  // Pull Phoenix's current year from the same tz as the note so the
  // "hide year for current year" heuristic isn't confused by browsers
  // running in a different tz on the New Year boundary.
  const yearFmt = new Intl.DateTimeFormat('en-US', { timeZone: TZ, year: 'numeric' });
  const noteYear = yearFmt.format(d);
  const nowYear = yearFmt.format(new Date());
  const date = d.toLocaleDateString('en-US', {
    timeZone: TZ,
    month: 'short',
    day: 'numeric',
    year: noteYear === nowYear ? undefined : 'numeric',
  });
  const time = d.toLocaleTimeString('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' });
  return `${date}, ${time}`;
}

export default function NotePad({ leadId = null, title = 'Notes', subtitle, compact = false, accentLabel }) {
  // ── Draft persistence ────────────────────────────────────────────────
  // The Lead Profile conditionally renders each tab — switching from
  // Notes → Income → Notes unmounts + remounts this component and would
  // otherwise lose whatever the LO had typed. We stash the draft (body +
  // any pending pasted images) in `localStorage` keyed by leadId so it
  // survives tab switches AND full-page refreshes for the current lead.
  // The draft is cleared on successful save (below) and naturally scoped
  // per-lead — visiting a different lead's Notes tab loads that lead's
  // draft (typically empty), not the previous lead's.
  const draftKey = `kf:note-draft:${leadId || 'global'}`;
  const readDraft = () => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return { body: '', pendingImages: [] };
      const parsed = JSON.parse(raw);
      return {
        body: typeof parsed?.body === 'string' ? parsed.body : '',
        pendingImages: Array.isArray(parsed?.pendingImages) ? parsed.pendingImages : [],
      };
    } catch {
      return { body: '', pendingImages: [] };
    }
  };
  const initialDraft = readDraft();
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState(initialDraft.body);
  const [pendingImages, setPendingImages] = useState(initialDraft.pendingImages); // data URLs awaiting save
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState(null); // currently-zoomed image src
  const fileInputRef = useRef(null);

  // Re-hydrate the draft when the leadId changes (e.g. navigating to
  // another lead's profile while this component instance is still
  // mounted somewhere upstream). Guarded so we don't clobber a brand-new
  // in-progress draft with an empty stored value.
  useEffect(() => {
    const d = readDraft();
    setBody(d.body);
    setPendingImages(d.pendingImages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  // Persist on every change. Skipped when both body + images are empty
  // so we don't pollute localStorage with empty entries.
  useEffect(() => {
    try {
      if (!body && pendingImages.length === 0) {
        localStorage.removeItem(draftKey);
      } else {
        localStorage.setItem(draftKey, JSON.stringify({ body, pendingImages }));
      }
    } catch {
      // Storage full / disabled — draft loss is a non-fatal degradation.
    }
  }, [body, pendingImages, draftKey]);

  // Close lightbox on Escape
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  const params = leadId ? { leadId } : { scope: 'global' };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(API, { params });
      setNotes(r.data || []);
    } catch (e) {
      toast.error('Could not load notes');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const text = body.trim();
    if ((!text && pendingImages.length === 0) || saving) return;
    setSaving(true);
    try {
      const r = await axios.post(API, {
        leadId: leadId || null,
        body: text,
        images: pendingImages,
      });
      setNotes((prev) => [r.data, ...prev]);
      setBody('');
      setPendingImages([]);
      // Draft is now persisted server-side — clear the localStorage copy
      // so the LO doesn't see the same text pre-filled the next time they
      // open this lead's Notes tab.
      try { localStorage.removeItem(draftKey); } catch { /* non-fatal */ }
      toast.success('Note saved');
      window.dispatchEvent(new CustomEvent('kf:notes-changed', { detail: { leadId: leadId || null } }));
    } catch {
      toast.error('Could not save note');
    } finally {
      setSaving(false);
    }
  };

  const ingestFiles = useCallback(async (files) => {
    const imageFiles = Array.from(files || []).filter((f) => f.type && f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    const accepted = [];
    for (const f of imageFiles) {
      if (f.size > MAX_IMG_BYTES) {
        toast.error(`${f.name || 'Image'} is over 5MB`);
        continue;
      }
      try {
        const dataUrl = await readFileAsDataURL(f);
        accepted.push(dataUrl);
      } catch {
        toast.error('Could not read image');
      }
    }
    if (accepted.length) {
      setPendingImages((prev) => [...prev, ...accepted].slice(0, MAX_IMAGES));
    }
  }, []);

  const onPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const it of items) {
      if (it.kind === 'file') {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length) {
      e.preventDefault();
      ingestFiles(files);
    }
  };

  const removePendingImage = (idx) => {
    setPendingImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const togglePin = async (n) => {
    try {
      const r = await axios.patch(`${API}/${n.id}`, { pinned: !n.pinned });
      setNotes((prev) => prev.map((x) => x.id === n.id ? r.data : x).sort((a, b) => (b.pinned - a.pinned) || (new Date(b.createdAt) - new Date(a.createdAt))));
    } catch {
      toast.error('Pin toggle failed');
    }
  };

  const remove = async (n) => {
    try {
      await axios.delete(`${API}/${n.id}`);
      setNotes((prev) => prev.filter((x) => x.id !== n.id));
    } catch {
      toast.error('Delete failed');
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      submit();
    }
  };

  return (
    <div className={cn('rounded-2xl border hairline bg-white overflow-hidden', !compact && 'p-6')}>
      {compact ? (
        <div className="px-5 py-3 border-b hairline flex items-center justify-between bg-neutral-50/40">
          <div className="flex items-center gap-2">
            <StickyNote className="h-3.5 w-3.5 text-[hsl(var(--kf-gold-deep))]" />
            <span className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">{title}</span>
            {accentLabel && <span className="text-[9.5px] font-bold uppercase tracking-wider bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] border border-[hsl(var(--kf-gold))]/30 rounded px-1.5 py-0.5">{accentLabel}</span>}
          </div>
          <span className="mono text-[10.5px] text-neutral-500">{notes.length}</span>
        </div>
      ) : (
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">Notes</div>
            <h3 className="display text-[18px] font-bold text-neutral-900 tracking-tight">{title}</h3>
            {subtitle && <p className="text-[12px] text-neutral-500 mt-0.5">{subtitle}</p>}
          </div>
          <span className="mono text-[11px] text-neutral-500">{notes.length} {notes.length === 1 ? 'note' : 'notes'}</span>
        </div>
      )}

      <form onSubmit={submit} className={cn(compact ? 'px-5 py-3 border-b hairline' : 'rounded-xl border hairline mb-4 p-4')}>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={onKey}
          onPaste={onPaste}
          rows={compact ? 3 : 6}
          placeholder={leadId ? 'Add a note about this lead… (paste images, ⌘/Ctrl+Enter to save)' : 'Quick scratchpad — anything you want to remember…'}
          className="w-full resize-y px-3.5 py-3 rounded-md bg-neutral-50 border hairline text-[14px] leading-relaxed placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 focus:bg-white min-h-[110px]"
        />
        {pendingImages.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="note-pending-images">
            {pendingImages.map((src, idx) => (
              <div key={idx} className="relative group">
                <img src={src} alt={`pasted-${idx}`} className="h-20 w-20 rounded-md object-cover border hairline" />
                <button
                  type="button"
                  onClick={() => removePendingImage(idx)}
                  className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-neutral-900 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition shadow"
                  aria-label="Remove image"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => { ingestFiles(e.target.files); e.target.value = ''; }}
        />
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[11px] text-neutral-400">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1 h-7 px-2 rounded-md border hairline bg-white hover:bg-neutral-50 text-neutral-700 font-semibold text-[11px]"
              data-testid="note-attach-image-btn"
            >
              <ImagePlus className="h-3.5 w-3.5" /> Attach
            </button>
            <span className="mono">⌘ + Enter to save · paste images</span>
          </div>
          <button type="submit" disabled={saving || (!body.trim() && pendingImages.length === 0)}
            className="h-9 px-4 rounded-md btn-ink text-[12.5px] font-bold flex items-center gap-1.5 disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Save note
          </button>
        </div>
      </form>

      <div className={cn('divide-y divide-neutral-100', !compact && 'rounded-xl border hairline overflow-hidden')}>
        {loading ? (
          <div className="px-5 py-6 text-center text-[12px] text-neutral-500 flex items-center justify-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading notes…
          </div>
        ) : notes.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <StickyNote className="h-5 w-5 text-neutral-300 mx-auto mb-1.5" />
            <div className="text-[12.5px] text-neutral-500">No notes yet — add the first one above.</div>
          </div>
        ) : (
          notes.map((n) => (
            <div key={n.id} className={cn('px-5 py-3 group hover:bg-neutral-50/50', n.pinned && 'bg-[hsl(var(--kf-gold-soft))]/30')}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {(n.body || n.pinned) && (
                    <div className="text-[13px] text-neutral-800 leading-relaxed whitespace-pre-wrap break-words">
                      {n.pinned && <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold text-[hsl(var(--kf-gold-deep))] mr-2"><Pin className="h-2.5 w-2.5" /> PINNED</span>}
                      <NoteBody body={n.body} />
                    </div>
                  )}
                  {Array.isArray(n.images) && n.images.length > 0 && (
                    <div className={cn('flex flex-wrap gap-2', n.body && 'mt-2')} data-testid="note-images">
                      {n.images.map((src, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setLightbox(src)}
                          className="block focus:outline-none focus:ring-2 focus:ring-neutral-900 rounded-md"
                          aria-label={`Open image ${i + 1}`}
                          data-testid={`note-image-${n.id}-${i}`}
                        >
                          <img
                            src={src}
                            alt={`note-img-${i}`}
                            className="max-h-48 max-w-[220px] rounded-md border hairline object-cover hover:opacity-90 transition cursor-zoom-in"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition shrink-0">
                  <button onClick={() => togglePin(n)} title={n.pinned ? 'Unpin' : 'Pin'}
                    className="h-7 w-7 rounded-md hover:bg-white border border-transparent hover:border-neutral-200 flex items-center justify-center text-neutral-500 hover:text-[hsl(var(--kf-gold-deep))]">
                    {n.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                  </button>
                  <button onClick={() => remove(n)} title="Delete"
                    className="h-7 w-7 rounded-md hover:bg-red-50 border border-transparent hover:border-red-200 flex items-center justify-center text-neutral-400 hover:text-red-600">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div className="mt-1.5 flex items-center gap-2 text-[10.5px] text-neutral-500 flex-wrap">
                <span className="font-semibold text-neutral-600">{n.author}</span>
                <span className="text-neutral-300">·</span>
                <span className="mono">{formatStamp(n.createdAt)}</span>
                <span className="text-neutral-300">·</span>
                <span className="mono text-neutral-400">{timeAgo(n.createdAt)}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {lightbox && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          data-testid="note-image-lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 cursor-zoom-out"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            aria-label="Close preview"
            className="absolute top-5 right-5 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={lightbox}
            alt="full-size note attachment"
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-2xl object-contain"
          />
        </div>,
        document.body
      )}
    </div>
  );
}
